import {
  ComponentType,
  type IComponentChild,
  type IComponentDefinition,
  type IDisplayItem,
  type IDisplayItemComponent
} from "@kuika/kuika-cl-model";

/**
 * WHICH component owns the "child items" editor for a selection, and WHAT counts as one of its
 * child items.
 *
 * ## Why this is its own module
 *
 * A table's columns, a menu's items, a tab's pages and a collapse's panels are not properties —
 * they are display items the catalog declares as the host's children, and the platform edits them
 * in a dedicated tree in the right panel rather than in the property form. That tree has to answer
 * two questions before it can draw anything:
 *
 *   1. Given what the user selected (often a cell deep inside a column), WHICH component is the
 *      host whose children are being edited?
 *   2. Is a given display item one of that host's declared child items, or ordinary content the
 *      user put inside it?
 *
 * Both answers lived inside the provider, reached through the provider's own screen. The workflow
 * screen builder needs the same two answers about a screen the designer does NOT have open, and a
 * second copy of the walk is how the two drift — a catalog change would be honoured in the right
 * panel and ignored in the Forms tab, for no reason a user could explain. So the rule lives here,
 * takes the dictionary and the catalog lookup as arguments, and both callers ask it.
 *
 * Pure: no provider, no screen object, no side effects.
 */

/** The catalog lookup, in the shape both the provider and a detached session already expose. */
export type ComponentDefinitionLookup = (item: IDisplayItemComponent | undefined) => IComponentDefinition | undefined;

/** The minimal dictionary shape this walk reads. */
export type ChildrenHostDictionary = Record<number, IDisplayItem | undefined>;

/**
 * Component types that can declare child items.
 *
 * `simple` is in the list on purpose and is not an oversight: several structural hosts (the
 * StaticTable family, Menu, some table columns) are marked `simple` in the catalog yet legitimately
 * own declared children. The catalog's `childrenInfo` is what actually decides; the type check only
 * narrows which entries are worth asking about.
 */
const CHILD_ITEM_HOST_TYPES: ReadonlySet<ComponentType> = new Set<ComponentType>([
  ComponentType.itemContainer,
  ComponentType.abstractItemContainer,
  ComponentType.responsiveRow,
  ComponentType.simple
]);

/**
 * Wrappers that exist only to hold one repeat slot's content.
 *
 * Walking up from a selection passes THROUGH these to reach the real host, unless the wrapper is
 * itself a host with declared children of its own (a TabItem holds content, a Table holds columns).
 */
const CHILD_ITEM_WRAPPERS: ReadonlySet<string> = new Set<string>([
  "ContentMenuPanel",
  "TabItem",
  "CollapsePanel",
  // KOA-13797 — a SplitterPanel is a fixed slot of its Splitter, exactly like a CollapsePanel.
  "SplitterPanel",
  "MenuItem",
  "SubMenu",
  "KanbanBoardLaneHeader",
  "KanbanBoardCard",
  "NodeTreeNode"
]);

/** Components that own declared children, so the walk stops AT them rather than passing through. */
const CHILD_ITEM_HOSTS: ReadonlySet<string> = new Set<string>([
  "Tab",
  "TabItem",
  "Collapse",
  "CollapsePanel",
  // KOA-13797 — the Splitter container and its panels. Both members on purpose: a SplitterPanel is
  // a wrapper AND a host, so the walk stops at it rather than passing through, which is the same
  // pair `Collapse`/`CollapsePanel` above already form.
  "Splitter",
  "SplitterPanel",
  "Menu",
  "Table",
  "ContentMenu",
  "ContentMenuPanel",
  "MenuItem",
  "TableRow",
  "TableColumn",
  "EditableTableColumn",
  "TableHeader",
  "TableDefaultColumn",
  "TableTemplateColumn",
  "KanbanBoardLane",
  "NodeTree"
]);

/**
 * Whether `di` is one of `host`'s DECLARED child items — the catalog's answer, not a guess.
 *
 * This is the platform's `isDIChildComponentForParent` rule; the provider now delegates to it.
 */
export function isDeclaredChildItem(
  di: IDisplayItem | undefined,
  host: IDisplayItem | undefined,
  getComponentDefinition: ComponentDefinitionLookup
): boolean {
  if (di === undefined || host === undefined) return false;
  const definition: IComponentDefinition | undefined = getComponentDefinition(di.item);
  if (definition?.name === undefined) return false;
  const hostDefinition: IComponentDefinition | undefined = getComponentDefinition(host.item);
  if (hostDefinition?.childrenInfo === undefined) return false;
  if (!CHILD_ITEM_HOST_TYPES.has(hostDefinition.componentType)) return false;
  return hostDefinition.childrenInfo.some((child: IComponentChild): boolean => child.name === definition.name);
}

export interface IDeclaredChildAnchor {
  /** The component the child must actually be added to. */
  host: IDisplayItem;
  /** The component originally asked for, when the answer is one hop below it. */
  redirectedFrom?: IDisplayItem;
}

/**
 * WHERE a child of this name actually goes, starting from the component the caller named.
 *
 * `resolveChildItemsHost` walks UP from a selection to find the host whose children are being
 * edited. This is the same question asked DOWNWARD: the caller has a host and a child name, and
 * the catalog may put that child one level below the component the caller means.
 *
 * A Table is the case that keeps costing us. Its `childrenInfo` is `TableHeader` and `TableRow`;
 * the COLUMNS are the row's children. "Add a column to this table" is what every author means and
 * it is never what the catalog says, so the add is refused for naming a child the host does not
 * declare — a refusal that is true and useless. The MCP render path already carries this redirect
 * (`component-tools.ts`, "Auto-redirect Table column adds to the Table's TableRow child"), where
 * its comment records the live cost: the model was told `ok` while the canvas never changed.
 *
 * Catalog-driven and one hop, rather than a list of the table family: the host is asked first, and
 * only if it does not declare the child is each existing direct child asked in tree order. A
 * component that declares the child itself is never redirected, so nothing that works today moves.
 */
export function resolveDeclaredChildAnchor(
  host: IDisplayItem | undefined,
  componentName: string,
  getComponentDefinition: ComponentDefinitionLookup
): IDeclaredChildAnchor | undefined {
  if (host === undefined) return undefined;

  const declares = (candidate: IDisplayItem | undefined): boolean => {
    const definition: IComponentDefinition | undefined = getComponentDefinition(candidate?.item);
    if (definition?.childrenInfo === undefined) return false;
    return definition.childrenInfo.some((child: IComponentChild): boolean => child.name === componentName);
  };

  if (declares(host)) return { host };

  const below: IDisplayItem | undefined = (host.children ?? []).find(declares);
  if (below === undefined) return undefined;
  return { host: below, redirectedFrom: host };
}

/**
 * The component whose child-items tree governs `kuikaId`.
 *
 * Walks up while the current item is a declared child of its parent, or sits inside a pure wrapper,
 * and stops at the first ancestor that can host declared children. Returns `undefined` when nothing
 * on the way up can — a plain Label has no child-items editor, and saying so is the honest answer.
 *
 * This is the platform's `setComponentChildrenSettingsMainDi` walk, with the provider's screen
 * replaced by the dictionary it is asked about.
 */
export function resolveChildItemsHost(
  dictionary: ChildrenHostDictionary | undefined,
  getComponentDefinition: ComponentDefinitionLookup,
  kuikaId: string | number | undefined | null
): IDisplayItem | undefined {
  if (dictionary === undefined || kuikaId === undefined || kuikaId === null) return undefined;
  const id: number = typeof kuikaId === "number" ? kuikaId : Number.parseInt(kuikaId, 10);
  if (!Number.isFinite(id)) return undefined;

  let current: IDisplayItem | undefined = dictionary[id];
  let parent: IDisplayItem | undefined = current?.parentId ? dictionary[current.parentId] : undefined;

  // Bounded: a corrupt parent chain must not spin. The depth is far beyond any real screen.
  let hops: number = 0;
  while (current?.parentId && hops < 256) {
    const parentName: string = parent?.item?.name ?? "";
    const throughWrapper: boolean = CHILD_ITEM_WRAPPERS.has(parentName) && !CHILD_ITEM_HOSTS.has(parentName);
    if (!isDeclaredChildItem(current, parent, getComponentDefinition) && !throughWrapper) break;
    current = dictionary[current.parentId];
    parent = current?.parentId ? dictionary[current.parentId] : undefined;
    hops += 1;
  }

  if (current === undefined) return undefined;
  const definition: IComponentDefinition | undefined = getComponentDefinition(current.item);
  if (definition === undefined || !CHILD_ITEM_HOST_TYPES.has(definition.componentType)) return undefined;
  return current;
}

/**
 * The size a freshly created table/report column needs to be visible at all.
 *
 * Shared with the drag-drop engine rather than restated: a column created from the child-items
 * editor and a column created by a drag must arrive the same size, and the two lists drifting
 * would show as "the column I added from the panel is invisible".
 *
 * A component is known by more than one name on the way in — the display item's own name and the
 * catalog definition's — and the drag path matched on EITHER, so both are accepted here and
 * neither call site can be the one that misses.
 */
export function applyChildItemSizeDefaults(
  di: IDisplayItem | undefined,
  ...componentNames: (string | undefined)[]
): void {
  const position = di?.styleObj?.position;
  if (position === undefined) return;
  const names: ReadonlySet<string> = new Set<string>(
    [...componentNames, di?.item?.name].filter((name: string | undefined): name is string => name !== undefined)
  );
  if (names.has("EditableTableColumn") || names.has("TableDefaultColumn")) {
    position.height = 60;
    return;
  }
  if (names.has("ReportTableColumn") || names.has("StaticTableColumn")) {
    position.width = 200;
    position.height = 40;
  }
}
